import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

// ---------------------------------------------------------------------------
// Modal stack + single global ESC listener
// ---------------------------------------------------------------------------
// Each mounted <ModalBackdrop> pushes an entry onto modalStack on mount and
// removes it on unmount. A single window-level keydown listener (installed
// once at module load) dispatches ESC to the topmost (last-pushed) entry
// only, so nested modals close in LIFO order: one ESC press closes the
// visually topmost layer and leaves lower layers visible. A second ESC press
// closes the next layer, and so on.
//
// Why a single global listener + stack instead of per-instance listeners:
// per-instance window listeners fire in registration order (oldest/bottom
// first), which closes the wrong modal first. Capture-phase listeners have
// the same ordering problem because multiple listeners on the same target
// run in registration order regardless of phase. A stack gives true LIFO
// semantics without depending on listener registration order.
//
// Backdrop-click dismissal remains per-instance (onClick on the backdrop
// div), because click events ARE position-scoped — clicking the MET backdrop
// hits MET's handler directly without involving any other modal. Only ESC
// needed the stack treatment.

const modalStack = [];

function handleGlobalKeydown(e) {
  if (e.key !== 'Escape') return;
  if (modalStack.length === 0) return;
  const topmost = modalStack[modalStack.length - 1];
  topmost.onClose();
}

if (typeof window !== 'undefined') {
  window.addEventListener('keydown', handleGlobalKeydown);
}

// ---------------------------------------------------------------------------
// Backdrop style
// ---------------------------------------------------------------------------

const backdropStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  background: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 10000
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function ModalBackdrop({ onClose, children }) {
  // Stable ref-wrapped entry. The SAME object is pushed onto the stack on
  // mount and removed on unmount; its `onClose` field is kept current during
  // render so the global keydown handler always calls the freshest callback
  // even when the parent passes a fresh inline arrow on each render. This
  // avoids churning the stack on every parent render (which would happen if
  // we keyed the effect on [onClose]).
  const entryRef = useRef({ onClose });
  entryRef.current.onClose = onClose;

  useEffect(() => {
    const entry = entryRef.current;
    modalStack.push(entry);
    document.body.style.overflow = 'hidden';
    return () => {
      const idx = modalStack.lastIndexOf(entry);
      if (idx !== -1) modalStack.splice(idx, 1);
      if (modalStack.length === 0) {
        document.body.style.overflow = '';
      }
    };
  }, []);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return createPortal(
    <div className="popup" style={backdropStyle} onClick={handleBackdropClick}>
      {children}
    </div>,
    document.body
  );
}

export default ModalBackdrop;
